import {
  Card,
  CardBody,
  CardHeader,
  Text,
  Grid,
  GridItem,
  Box,
  Flex,
  Input,
  Button,
} from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { formatDateWithNoHours } from "@/utils/formatDateWithNoHours";
import { RelatorioConciliacao } from "@/models/consignado/relatorioConciliacao.model";

export interface GranatechInternalDataCardProps {
  relatorios: RelatorioConciliacao[] | null
  handleConciliateClick: () => void;
  isLoading: boolean;
  dynamicSteps: any[];
  editedValues: {
    shortHash: string;
    valorLiberado: number;
    valorParcela: number;
    quantidadeParcela: number;
    valorIof: number;
    insuranceCompanyName: string;
    valorSeguro: number;
    valorDesconto: number;
    dataCriacaoProposta: string;
  };
  editingFields: {
    shortHash: boolean;
    valorLiberado: boolean;
    valorParcela: boolean;
    quantidadeParcela: boolean;
    valorIof: boolean;
    insuranceCompanyName: boolean;
    valorSeguro: boolean;
    valorDesconto: boolean;
    dataCriacaoProposta: boolean;
  };
  monetaryInputs: {
    valorLiberado: string;
    valorParcela: string;
    valorIof: string;
    valorSeguro: string;
    valorDesconto: string;
  };
  handleToggleAllFields: () => void;
  onChangeField: (field: "shortHash" | "valorLiberado" | "valorParcela" | "quantidadeParcela" | "valorIof" | "insuranceCompanyName" | "valorSeguro" | "valorDesconto" | "dataCriacaoProposta", value: string) => void;
}

type EditableField = keyof GranatechInternalDataCardProps["editedValues"];
type MonetaryField = keyof GranatechInternalDataCardProps["monetaryInputs"];

const monetaryFieldsSet = new Set<EditableField>([
  "valorLiberado",
  "valorParcela",
  "valorIof",
  "valorSeguro",
  "valorDesconto",
]);

const isMonetaryField = (field: EditableField): field is MonetaryField => {
  return monetaryFieldsSet.has(field);
};

const formatDateToInputValue = (value: string): string => {
  if (!value)
    return "";

  const date = new Date(value);

  if (!Number.isNaN(date.getTime()))
    return date.toISOString().slice(0, 10);

  return value.split(" ")[0] || "";
};

export default function GranatechInternalDataCard({
  editedValues,
  editingFields,
  monetaryInputs,
  handleToggleAllFields,
  onChangeField,
  relatorios,
  handleConciliateClick,
  isLoading,
  dynamicSteps
}: GranatechInternalDataCardProps) {

  const renderFieldHeader = (label: string) => (
    <Flex alignItems="center" justifyContent="space-between" mb={1}>
      <Text fontSize="xs" color="gray.500" textTransform="uppercase">
        {label}
      </Text>
    </Flex>
  );

  const renderFieldContent = (
    field: EditableField,
    displayValue: string,
    inputType: "text" | "number" | "date" = "text"
  ) => {
    if (!editingFields[field]) {
      return (
        <Text fontSize="md" color="gray.800">
          {displayValue}
        </Text>
      );
    }

    return (
      <Input
        size="sm"
        type={isMonetaryField(field) ? "text" : inputType}
        inputMode={isMonetaryField(field) ? "numeric" : undefined}
        value={
          isMonetaryField(field)
            ? monetaryInputs[field]
            : field === "dataCriacaoProposta"
              ? formatDateToInputValue(String(editedValues[field] ?? ""))
              : String(editedValues[field] ?? "")
        }
        onChange={(event) => onChangeField(field, event.target.value)}
      />
    );
  };

  return (
    <Card p={6} bg="white" borderRadius="lg" shadow="sm" borderWidth="2px" borderColor="gray.200">
        <CardHeader p={0} mb={6}>
          <Flex justify="space-between" align="center">
            <Text fontSize="md" fontWeight="bold" color="gray.700">
              CONTRATAÇÃO (DADOS INTERNOS)
            </Text>
              
          <Flex alignItems='center' gap={2}>
            <Text fontSize="md">
              Short Hash: #{editedValues.shortHash || "-"}
            </Text>

            <Button 
              leftIcon={<EditIcon />}
              size='sm'
              colorScheme="primary"
              onClick={handleToggleAllFields}
            >
              Editar
            </Button>
            <Button 
              px={4}
              size='sm'
              colorScheme="primary"
              onClick={handleConciliateClick}
              isDisabled={dynamicSteps.length < 1}
              isLoading={isLoading}
              loadingText="Conciliando..."
            >
              {relatorios && relatorios.length > 0 ? "Reconciliar" : "Conciliar"}
            </Button>
          </Flex>
          </Flex>
        </CardHeader>
        <CardBody p={0}>
          <Grid templateColumns="repeat(4, 1fr)" gap={6}>
            <GridItem>
              <Box>
                {renderFieldHeader("Valor Liberado")}
                {renderFieldContent(
                  "valorLiberado",
                  BrlCurrencyFormatter(editedValues.valorLiberado),
                  "number"
                )}
              </Box>
            </GridItem>

            <GridItem>
              <Box>
                {renderFieldHeader("Valor Parcela")}
                {renderFieldContent(
                  "valorParcela",
                  BrlCurrencyFormatter(editedValues.valorParcela),
                  "number"
                )}
              </Box>
            </GridItem>

            <GridItem>
              <Box>
                {renderFieldHeader("Parcelas")}
                {renderFieldContent(
                  "quantidadeParcela",
                  `${editedValues.quantidadeParcela}x`,
                  "number"
                )}
              </Box>
            </GridItem>

            <GridItem>
              <Box>
                {renderFieldHeader("IOF")}
                {renderFieldContent(
                  "valorIof",
                  BrlCurrencyFormatter(editedValues.valorIof),
                  "number"
                )}
              </Box>
            </GridItem>
            <GridItem>
              <Box>
                {renderFieldHeader("Seguradora")}
                {renderFieldContent(
                  "insuranceCompanyName",
                  editedValues.insuranceCompanyName || "S/N"
                )}
              </Box>
            </GridItem>
            <GridItem>
              <Box>
                {renderFieldHeader("Valor Seguro")}
                {renderFieldContent(
                  "valorSeguro",
                  editedValues.valorSeguro === null
                    ? "Não se aplica"
                    : editedValues.valorSeguro
                    ? BrlCurrencyFormatter(editedValues.valorSeguro)
                    : "S/N",
                  "number"
                )}
              </Box>
            </GridItem>

            <GridItem>
              <Box>
                {renderFieldHeader("Valor Descontado")}
                {renderFieldContent(
                  "valorDesconto",
                  BrlCurrencyFormatter(editedValues.valorDesconto),
                  "number"
                )}
              </Box>
            </GridItem>

            <GridItem>
              <Box>
                {renderFieldHeader("Data Proposta")}
                {renderFieldContent(
                  "dataCriacaoProposta",
                  editedValues.dataCriacaoProposta
                    ? formatDateWithNoHours(editedValues.dataCriacaoProposta)
                    : "Data não disponível",
                  "date"
                )}
              </Box>
            </GridItem>
          </Grid>

        </CardBody>
    </Card>
  );
}
